import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { FabricLobbyConfigService } from "@obg/gaming.external";
import { CoreHttpClient, CoreState, Customer, MarketSelector } from "@obg/ngx.core";
import { CacheFactory, CacheStore, FeatureFlag, FeatureFlagService } from "@obg/ngx.global";
import { CookieService } from "@obg/ngx.global.evo";
import { Observable } from "rxjs";
import { map, tap } from "rxjs/operators";

import { GameConfigService } from "./game.config";
import { EXPERIMENT_FLAGS, FETCH_ONLY_NEEDED_GAMES_FLAG_KEY } from "./game.const";
import { FetchOnlyNeededGamesFlag, Game, GameDetail } from "./game.model";
import { getGamesByCollectionIdCacheKey, getGamesCacheKey } from "./game.util";

@Injectable({
	providedIn: "root",
})
export class GameService {
	private cache: CacheStore;
	private includeHiddenGames = false;
	private marketLanguageCode: string | undefined;

	constructor(
		private httpClient: CoreHttpClient,
		private config: GameConfigService,
		cacheFactory: CacheFactory,
		private cookieService: CookieService,
		private store: Store<CoreState>,
		private marketSelector: MarketSelector,
		private featureFlagService: FeatureFlagService,
		private fabricLobbyConfigService: FabricLobbyConfigService
	) {
		this.cache = cacheFactory.get("gaming.game", {
			isLanguageSpecific: true,
		});

		this.includeHiddenGames = this.isExperimentHiddenLiveEnabled();

		this.store.select(this.marketSelector.getLanguageCode()).pipe(
			tap((marketCode) => {
				this.marketLanguageCode = marketCode;
			})
		).subscribe;
	}

	getAll(customer: Customer | undefined): Observable<Record<string, Game>> {
		const countryCode = customer?.address?.country;
		const customerId = customer?.customerId;
		if (this.fabricLobbyConfigService.isFabricLobbyEnabled()) {
			return this.fetchGames(countryCode)();
		}
		return this.cache.getOrSetAsObservable(
			getGamesCacheKey(customerId, countryCode ?? this.marketLanguageCode, this.includeHiddenGames),
			this.fetchGames(countryCode),
			{ maxAge: this.config.getCacheMaxAgeForGetAll() }
		);
	}

	getDetailed(gameId: string): Observable<GameDetail> {
		return this.cache.getOrSetAsObservable(
			`game:${gameId}`,
			() =>
				this.httpClient
					.get<GameDetail>({
						url: this.config.getGameDetailEndpoint(),
						searchParams: { gameId },
					})
					.pipe(map((gameDetail) => this.transformTags(gameDetail))),
			{ maxAge: this.config.getCacheMaxAgeForGetAll() }
		);
	}

	getByCollection(
		collectionId: string,
		customer: Customer | undefined,
		topCount = 20
	): Observable<Record<string, Game>> {
		const customerId = customer?.customerId;
		const countryCode = customer?.address?.country;

		return this.cache.getOrSetAsObservable(
			getGamesByCollectionIdCacheKey(
				collectionId,
				customerId,
				countryCode ?? this.marketLanguageCode,
				this.includeHiddenGames
			),
			this.fetchGamesByCollectionId(collectionId, countryCode, topCount),
			{ maxAge: this.config.getCacheMaxAgeForGetAll() }
		);
	}

	getGamesByGameIds(ids: string[]): Observable<Record<string, Game>> {
		return this.httpClient
			.post<Game[]>({
				url: this.config.getGamesByGameIdsEndpoint(),
				data: ids,
			})
			.pipe(map((games) => this.normalize(games)));
	}

	getFetchOnlyNeededGamesFeatureFlag(): FeatureFlag<FetchOnlyNeededGamesFlag | undefined> | undefined {
		return this.featureFlagService.get<FetchOnlyNeededGamesFlag | undefined>(FETCH_ONLY_NEEDED_GAMES_FLAG_KEY);
	}

	/**
	 * Returns the method to call if there is no cacheHit for games cache
	 *
	 * @param countryCode
	 * @returns
	 */
	private fetchGames(countryCode: string | undefined) {
		return () =>
			this.httpClient
				.get<Game[]>({
					url: this.config.getGameEndpoint(),
					searchParams: { countryCode },
				})
				.pipe(map((games) => this.normalize(games)));
	}

	private fetchGamesByCollectionId(collectionId: string, countryCode: string | undefined, topCount: number) {
		return () =>
			this.httpClient
				.get<Game[]>({
					url: this.config.getGameByCollectionEndpoint(),
					searchParams: { collectionId, countryCode, topCount },
				})
				.pipe(map((games) => this.normalize(games)));
	}

	/**
	 * Normalizes the games from {Array<Game>} to {Record<string, Game>}.
	 *
	 * todo: in the future we should evaluate whether this should be done by the API.
	 */
	private normalize(games: Game[]): Record<string, Game> {
		const normalized: Record<string, Game> = {};

		for (const game of games) {
			normalized[game.id] = this.transformTags(game);
		}

		return normalized;
	}

	/**
	 * Uses `isHot` and `isNew` properties of the given game and populates the array `tags`
	 * with the respective property names when their value is true.
	 *
	 * todo: in the future we should evaluate whether this should be done by the API.
	 * todo: consider using the already existing property `metaTags`.
	 */
	private transformTags(game: Game): Game {
		game.tags = [];

		if (game.isHot) {
			game.tags.push("hot");
		}

		if (game.isNew) {
			game.tags.push("new");
		}

		return game;
	}

	private isExperimentHiddenLiveEnabled() {
		const experiments = this.cookieService.get("obg-experiments");
		const isIncludeHiddenGames = !!experiments?.toLowerCase().includes(EXPERIMENT_FLAGS.HIDDEN_LIVE);
		return isIncludeHiddenGames;
	}
}
